import { useForm } from 'react-hook-form';
import { PreviewFormValues } from 'features/join/publisher/JoinPreview';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import debounce from 'lodash.debounce';
import {
  addDeviceChangeListener,
  enumerateDevices,
  getDefaultMediaDevice,
  getDevicePermissions,
  removeDeviceChangeListener,
} from 'features/user-media/utils';
import { findDeviceDifferences } from 'features/user-media/utils/findDeviceDifferences';
import { EnumeratedMediaDevices, UnwrappedMediaDeviceInfo } from 'features/user-media/types';
import * as Sentry from '@sentry/react';
import { activeMediaDeviceUpdated, mediaDevicesUpdated } from 'features/user-media/userMediaSlice';
import { useAppDispatch } from 'store/hooks';
import { useMediaDevices } from 'hooks/useMediaDevices';
import { useBroadcastSetup } from 'utils/broadcast-setup/context';
import { usePermissions } from 'features/permissions/usePermissions';
import { PermissionTypes } from 'features/permissions/types';
import { logger } from 'utils/logger';

// This hook manages preview form values and synchronizes broadcast state with media device changes
export const useBroadcastSetupPreview = () => {
  const appDispatch = useAppDispatch();

  const { mediaDevices, activeMediaDevices, micPermissions, camPermissions } = useMediaDevices();

  const { hasPermissions } = usePermissions();
  const canBroadcastAudio = hasPermissions(PermissionTypes.broadcastAudio);
  const canBroadcastVideo = hasPermissions(PermissionTypes.broadcastVideo);

  const {
    audio,
    video,
    audioStream,
    videoStream,
    dispatch,
    disableAudio,
    enableAudio,
    enableCamera,
    disableCamera,
  } = useBroadcastSetup();

  const formMethods = useForm<PreviewFormValues>({
    defaultValues: {
      audioinput: activeMediaDevices.audioinput || '',
      audiooutput: activeMediaDevices.audiooutput || '',
      videoinput: activeMediaDevices.videoinput || '',
    },
  });

  const { setValue } = formMethods;

  const activateMic = useCallback(
    async (deviceId: string) => {
      await enableAudio(deviceId);
      setValue('audioinput', deviceId);
    },
    [enableAudio, setValue]
  );

  const deactivateMic = useCallback(() => {
    dispatch({ type: 'mediaDeactivated', payload: 'audio' });
  }, [dispatch]);

  const activateCamera = useCallback(
    async (deviceId: string) => {
      await enableCamera(deviceId);
      setValue('videoinput', deviceId);
    },
    [enableCamera, setValue]
  );

  const deactivateCamera = useCallback(() => {
    dispatch({ type: 'mediaDeactivated', payload: 'video' });
  }, [dispatch]);

  // Store frequently changing values in refs to avoid re-registering the listener
  const deviceContextRef = useRef({
    mediaDevices,
    activeMediaDevices,
    isAudioActive: audio.active,
    isVideoActive: video.active,
    micPermissions,
    camPermissions,
    canBroadcastAudio,
    canBroadcastVideo,
  });

  deviceContextRef.current = {
    mediaDevices,
    activeMediaDevices,
    isAudioActive: audio.active,
    isVideoActive: video.active,
    micPermissions,
    camPermissions,
    canBroadcastAudio,
    canBroadcastVideo,
  };

  const handleRemovedAudioInput = useCallback(
    async (updatedDevices: EnumeratedMediaDevices, removedDevice: UnwrappedMediaDeviceInfo) => {
      const deviceContext = deviceContextRef.current;

      try {
        const deviceId = getDefaultMediaDevice(updatedDevices, 'audioinput');

        if (deviceContext.isAudioActive) {
          if (deviceContext.canBroadcastAudio) {
            if (!deviceId) {
              logger.remote().info('[Broadcast Setup] No replacement audio device found', {
                from: removedDevice.label,
              });
              deactivateMic();
            } else {
              logger.remote().info('[Broadcast Setup] Auto-switched to new audio device', {
                from: removedDevice.label,
                to: deviceId,
              });

              disableAudio();
              await activateMic(deviceId);
            }
          } else {
            logger.remote().info('[Broadcast Setup] Audio blocked - no broadcast permission');
            deactivateMic();
          }
        } else if (deviceId) {
          setValue('audioinput', deviceId);
          appDispatch(
            activeMediaDeviceUpdated({
              kind: 'audioinput',
              id: deviceId,
            })
          );
        }
      } catch (error) {
        logger.remote().error('[Broadcast Setup] Error handling removed audio device', error);
        Sentry.captureException(error);
        deactivateMic();
      }
    },
    [activateMic, deactivateMic, disableAudio, appDispatch, setValue]
  );

  const handleRemovedVideoInput = useCallback(
    async (updatedDevices: EnumeratedMediaDevices, removedDevice: UnwrappedMediaDeviceInfo) => {
      const deviceContext = deviceContextRef.current;

      try {
        const deviceId = getDefaultMediaDevice(updatedDevices, 'videoinput');

        if (deviceContext.isVideoActive) {
          if (deviceContext.canBroadcastVideo) {
            if (!deviceId) {
              logger.remote().info('[Broadcast Setup] No replacement video device found', {
                from: removedDevice.label,
              });
              deactivateCamera();
            } else {
              logger.remote().info('[Broadcast Setup] Auto-switched to new video device', {
                from: removedDevice.label,
                to: deviceId,
              });

              await disableCamera();
              await activateCamera(deviceId);
            }
          } else {
            logger.remote().info('[Broadcast Setup] Video blocked - no broadcast permission');
            deactivateCamera();
          }
        } else if (deviceId) {
          setValue('videoinput', deviceId);
          appDispatch(
            activeMediaDeviceUpdated({
              kind: 'videoinput',
              id: deviceId,
            })
          );
        }
      } catch (error) {
        logger.remote().error('[Broadcast Setup] Error handling removed video device', error);
        Sentry.captureException(error);
        deactivateCamera();
      }
    },
    [activateCamera, deactivateCamera, disableCamera, appDispatch, setValue]
  );

  const handleAddedAudioInput = useCallback(
    async (updatedDevices: EnumeratedMediaDevices) => {
      const deviceContext = deviceContextRef.current;

      try {
        const devicePermissions = getDevicePermissions(updatedDevices, 'audioinput');

        if (devicePermissions === 'granted' && deviceContext.canBroadcastAudio) {
          const deviceId = getDefaultMediaDevice(updatedDevices, 'audioinput');

          if (!deviceId) {
            logger.remote().info('[Broadcast Setup] No audio device found after device added');
          } else {
            logger.remote().info('[Broadcast Setup] Auto-activated audio device', {
              deviceId,
            });

            await activateMic(deviceId);
          }
        }
      } catch (error) {
        logger.remote().error('[Broadcast Setup] Error activating added audio device', error);
        Sentry.captureException(error);
        deactivateMic();
      }
    },
    [activateMic, deactivateMic]
  );

  const handleAddedVideoInput = useCallback(
    async (updatedDevices: EnumeratedMediaDevices) => {
      const deviceContext = deviceContextRef.current;

      try {
        const devicePermissions = getDevicePermissions(updatedDevices, 'videoinput');

        if (devicePermissions === 'granted' && deviceContext.canBroadcastVideo) {
          const deviceId = getDefaultMediaDevice(updatedDevices, 'videoinput');

          if (!deviceId) {
            logger.remote().info('[Broadcast Setup] No video device found after device added');
          } else {
            logger.remote().info('[Broadcast Setup] Auto-activated video device', {
              deviceId,
            });

            await activateCamera(deviceId);
          }
        }
      } catch (error) {
        logger.remote().error('[Broadcast Setup] Error activating added video device', error);
        Sentry.captureException(error);
        deactivateCamera();
      }
    },
    [activateCamera, deactivateCamera]
  );

  const handleAudioOutputChange = useCallback(
    (updatedDevices: EnumeratedMediaDevices) => {
      const deviceId = getDefaultMediaDevice(updatedDevices, 'audiooutput');

      if (!deviceId) {
        logger.remote().info('[Broadcast Setup] No replacement audio output device found');
        return;
      }

      appDispatch(
        activeMediaDeviceUpdated({
          kind: 'audiooutput',
          id: deviceId,
        })
      );

      setValue('audiooutput', deviceId);
    },
    [appDispatch, setValue]
  );

  const handleDeviceChange = useCallback(async () => {
    const deviceContext = deviceContextRef.current;
    const updatedDevices = await enumerateDevices();
    const { removedActiveDevices, addedDevices } = findDeviceDifferences(
      updatedDevices,
      deviceContext.mediaDevices,
      deviceContext.activeMediaDevices
    );

    if (removedActiveDevices.audioinput) {
      await handleRemovedAudioInput(updatedDevices, removedActiveDevices.audioinput);
    }

    if (removedActiveDevices.videoinput) {
      await handleRemovedVideoInput(updatedDevices, removedActiveDevices.videoinput);
    }

    if (deviceContext.micPermissions !== 'granted' && addedDevices.audioinput) {
      await handleAddedAudioInput(updatedDevices);
    }

    if (deviceContext.camPermissions !== 'granted' && addedDevices.videoinput) {
      await handleAddedVideoInput(updatedDevices);
    }

    if (removedActiveDevices.audiooutput || addedDevices.audiooutput) {
      handleAudioOutputChange(updatedDevices);
    }

    appDispatch(
      mediaDevicesUpdated(updatedDevices, {
        updatePermissions: true,
      })
    );
  }, [
    handleRemovedAudioInput,
    handleRemovedVideoInput,
    handleAddedAudioInput,
    handleAddedVideoInput,
    handleAudioOutputChange,
    appDispatch,
  ]);

  const debouncedDeviceChange = useMemo(
    () => debounce(handleDeviceChange, 300),
    [handleDeviceChange]
  );

  useEffect(() => {
    addDeviceChangeListener(debouncedDeviceChange);
    return () => {
      removeDeviceChangeListener(debouncedDeviceChange);
      debouncedDeviceChange.cancel();
    };
  }, [debouncedDeviceChange]);

  useEffect(() => {
    const videoTrack = videoStream?.getVideoTracks()[0];
    if (!videoTrack) {
      return undefined;
    }

    const listener = async () => {
      const updatedDevices = await enumerateDevices();
      const devicePermissions = getDevicePermissions(updatedDevices, 'videoinput');

      if (devicePermissions !== 'granted') {
        appDispatch(
          mediaDevicesUpdated(updatedDevices, {
            updateActiveDevices: true,
            updatePermissions: true,
          })
        );

        setValue('videoinput', '');
        deactivateCamera();
        await disableCamera();
      }
    };

    videoTrack.addEventListener('ended', listener);

    return () => {
      videoTrack.removeEventListener('ended', listener);
    };
  }, [deactivateCamera, disableCamera, appDispatch, setValue, videoStream]);

  useEffect(() => {
    const audioTrack = audioStream?.getAudioTracks()[0];
    if (!audioTrack) {
      return undefined;
    }

    const listener = async () => {
      const updatedDevices = await enumerateDevices();
      const devicePermissions = getDevicePermissions(updatedDevices, 'audioinput');

      if (devicePermissions !== 'granted') {
        appDispatch(
          mediaDevicesUpdated(updatedDevices, {
            updateActiveDevices: true,
            updatePermissions: true,
          })
        );

        setValue('audioinput', '');
        setValue('audiooutput', '');
        deactivateMic();
        disableAudio();
      }
    };

    audioTrack.addEventListener('ended', listener);

    return () => {
      audioTrack.removeEventListener('ended', listener);
    };
  }, [deactivateMic, disableAudio, appDispatch, setValue, audioStream]);

  return formMethods;
};
